import React, { ReactNode, RefCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TextAreaWithRef } from "@/components/forms/inputsWithRef/textAreaWithRef";
import CrudFieldController from "@/components/tables/fields/CrudFieldController";
import {
  isInputBlockedByRule,
  maxLengthRule,
  patternRule,
  requiredRule,
} from "@/components/tables/fields/fieldRules";
import { textAreaFieldStyle } from "@/components/tables/fields/fieldStyles";
import { composeInputRef } from "@/components/tables/fields/validationTarget";
import type { CrudModalField } from "@/components/tables/fields/crudModalField";
import styles from "@/components/tables/fields/crudField.module.css";

export interface CrudTextareaFieldProps {
  field: Pick<
    CrudModalField,
    | "id"
    | "header"
    | "required"
    | "errorMessage"
    | "rule"
    | "errorMessageValidation"
    | "maxLength"
    | "maxLengthMessage"
    | "textareaRows"
    | "hideField"
    | "blockInvalidInput"
  >;
  modalMode?: string;
  registerValidationTarget?: (name: string) => RefCallback<HTMLElement>;
  additionalComponentForModal?: ReactNode;
  onChange?: (value: string) => void;
}

const CrudTextareaField = ({
  field,
  modalMode,
  registerValidationTarget,
  additionalComponentForModal,
  onChange,
}: CrudTextareaFieldProps) => {
  const name = field.id as string;
  const { t } = useTranslation();
  const { control } = useFormContext();

  if (field.hideField) return null;

  return (
    <>
      <CrudFieldController
        name={name}
        control={control}
        rules={{
          ...patternRule(field, t),
          ...requiredRule(field.required, field.errorMessage),
          ...maxLengthRule(field),
        }}
        render={({
          field: { onChange: setFormValue, value, ...rest },
          error,
        }) => (
          <TextAreaWithRef
            {...rest}
            ref={composeInputRef(
              rest.ref,
              registerValidationTarget?.(rest.name)
            )}
            label={field.header}
            required={field.required}
            name={field.id}
            onChange={(event: any) => {
              const nextValue = event.target.value;
              if (isInputBlockedByRule(field, nextValue)) return;
              onChange?.(nextValue);
              setFormValue(nextValue);
            }}
            value={value ?? ""}
            wrapperClassname={`${styles.textField} ${styles.preWhiteSpace}`}
            rows={field.textareaRows ? field.textareaRows : 8}
            fieldStyle={textAreaFieldStyle(!!error)}
          />
        )}
      />
      {additionalComponentForModal &&
        modalMode === "edit" &&
        additionalComponentForModal}
    </>
  );
};

export default CrudTextareaField;
